import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SlidingPane from 'react-sliding-pane';
import Popup from 'reactjs-popup';
import client from '../../../shared/api/client';
import { toastSuccess, toastError } from '../../../shared/toastHelper';
import TeamPagination from '../../TeamManager/components/TeamPagination';
import Metric from './Metric';
import AutomationRules from './AutomationRules';
import RiskDesk from '../../RiskDesk/components/RiskDesk';

const PER_PAGE = 25;

const TIER = {
  issuer: { label: 'Issuer alert', bg: 'var(--sl-bg-error-primary)', border: 'var(--sl-border-error)', text: 'var(--sl-text-error)', dot: 'var(--sl-text-error)' },
  high: { label: 'High risk', bg: 'var(--sl-bg-warning-primary)', border: 'var(--sl-border-warning)', text: 'var(--sl-text-warning)', dot: 'var(--sl-text-warning)' },
  pattern: { label: 'Pattern flag', bg: 'var(--sl-bg-brand-primary)', border: 'var(--sl-border-brand)', text: 'var(--sl-text-brand)', dot: 'var(--sl-fg-brand)' },
};

const FILTERS = [
  { key: 'open', label: 'All open' },
  { key: 'issuer', label: 'Issuer alerts' },
  { key: 'pattern', label: 'Pattern flags' },
  { key: 'resolved', label: 'Resolved' },
];

const EVENT_SCOPES = [
  { key: 'all', label: 'All events' },
  { key: 'on_sale', label: 'On-sale events' },
  { key: 'past', label: 'Past events' },
];

const SEVERITY_DOT = {
  high: 'var(--sl-text-error)',
  medium: 'var(--sl-text-warning)',
  low: 'var(--sl-text-tertiary)',
};

const SR_ONLY = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 };

// Tolerate the pre-Phase-2 string shape so a stale browser tab across a deploy degrades gracefully.
const normalizeSignal = (sig) => (typeof sig === 'string' ? { label: sig } : (sig || {}));

const SECTION_LABEL = { fontSize: 12, fontWeight: 600, color: 'var(--sl-text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 };
const CARD = { background: 'var(--sl-bg-primary)', border: '1px solid var(--sl-border-secondary)', borderRadius: 12 };
const BTN_SECONDARY = { background: 'var(--sl-bg-primary)', color: 'var(--sl-text-secondary)', border: '1px solid var(--sl-border-primary)' };
const BTN_DANGER = { background: 'var(--sl-bg-error-solid)', color: 'var(--sl-text-white)', border: 'none' };

const riskColor = (score) => (score >= 75 ? 'var(--sl-text-error)' : score >= 50 ? 'var(--sl-text-warning)' : 'var(--sl-text-quaternary)');
const riskLabel = (score) => (score >= 75 ? 'High' : score >= 50 ? 'Elevated' : 'Normal');
const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const NO_CHARGEBACKS = { count: 0, cents: 0, median_days: null };
const chargedBackSub = (summary, fmt) => {
  if (summary.median_days == null) return fmt(summary.cents);
  const days = summary.median_days < 1 ? 'under 1 day' : `${Math.round(summary.median_days)} ${Math.round(summary.median_days) === 1 ? 'day' : 'days'}`;
  return `${fmt(summary.cents)} · median ${days} from flag`;
};
const REFUNDED_OUTCOMES = ['refunded', 'refunded_as_fraudulent', 'refunded_as_fraud'];
const resolvedLabel = (row) => {
  if (row.status === 'open') return '';
  if (REFUNDED_OUTCOMES.includes(row.decision)) return 'Refunded';
  if (row.closed_reason === 'disputed') return 'Charged back';
  if (REFUNDED_OUTCOMES.includes(row.closed_reason)) return 'Refunded';
  return 'Dismissed';
};

const relativeReceived = (iso) => {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

const RiskChargebacks = ({ team, canAdminister, currencyCode, metrics, blockListDefault, digest, rulesAvailable, automationEnabled, autoRefundAvailable, signalAction, riskRows }) => {
  const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode });
  const fmt = (cents) => money.format((cents || 0) / 100);

  const [rows, setRows] = useState([]);
  const [metricsData, setMetrics] = useState(metrics);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_entries: 0, per_page: PER_PAGE });
  const [filter, setFilter] = useState('open');
  const [view, setView] = useState('flagged');
  const [eventScope, setEventScope] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [blocklist, setBlocklist] = useState(blockListDefault);
  const [digestSubscribed, setDigestSubscribed] = useState(digest.subscribed);
  const [busy, setBusy] = useState(false);
  const requestId = useRef(0);

  const selected = rows.find((r) => r.id === selectedId) || null;
  const confirmRow = rows.find((r) => r.id === confirmId) || null;

  const fetchData = useCallback(() => {
    const myId = requestId.current + 1;
    requestId.current = myId;
    setLoading(true);
    setLoadError(false);
    client.get(`/teams/${team.id}/payment_reviews.json`, { params: { filter, event_scope: eventScope, page, per_page: PER_PAGE } })
      .then((res) => {
        if (myId !== requestId.current) return;
        setRows(res.data.payment_reviews || []);
        setPagination(res.data.pagination || { current_page: 1, total_pages: 1, total_entries: 0, per_page: PER_PAGE });
        if (res.data.metrics) setMetrics(res.data.metrics);
      })
      .catch(() => {
        if (myId !== requestId.current) return;
        setLoadError(true);
        toastError('Could not load flagged orders. Please try again.');
      })
      .finally(() => { if (myId === requestId.current) setLoading(false); });
  }, [team.id, filter, eventScope, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectFilter = (key) => { setFilter(key); setPage(1); };
  const selectEventScope = (e) => { setEventScope(e.target.value); setPage(1); };

  const post = async (path, body) => {
    setBusy(true);
    try {
      await client.post(`/teams/${team.id}/payment_reviews/${path}`, body);
    } finally { setBusy(false); }
  };

  const doRefund = async () => {
    const row = confirmRow;
    try {
      await post(`${row.id}/refund`, { fraudulent: true, block_list: blocklist });
      setConfirmId(null); setSelectedId(null);
      fetchData();
      toastSuccess(`Order refunded — tickets invalidated${blocklist ? ' and buyer added to the block list' : ''}.`);
    } catch (e) {
      toastError(e.response?.data?.error || 'Refund failed. Please try again.');
    }
  };

  const doDismiss = async (row) => {
    try {
      await post(`${row.id}/approve`, {});
      setSelectedId(null);
      fetchData();
      toastSuccess('Flag dismissed — the order was marked as legitimate.');
    } catch (e) {
      toastError(e.response?.data?.error || 'Could not dismiss the flag. Please try again.');
    }
  };

  const tierBadge = (tier) => {
    const t = TIER[tier] || TIER.pattern;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 9999, fontSize: 12, fontWeight: 600, border: `1px solid ${t.border}`, background: t.bg, color: t.text, whiteSpace: 'nowrap' }}>
        <span style={{ width: 8, height: 8, borderRadius: 9999, background: t.dot }} />{t.label}
      </span>
    );
  };

  const renderDrawer = () => {
    if (!selected) return null;
    const s = selected;
    const inWindow = s.refund_window_tone === 'in_window';
    const hasScore = s.risk_score != null;
    const isEfw = s.source === 'early_fraud_warning';
    const cardLine = s.card_brand ? `${titleCase(s.card_brand)} •••• ${s.card_last4}` : null;
    const bannerColor = inWindow ? 'var(--sl-fg-brand)' : 'var(--sl-text-warning)';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '20px 24px', borderBottom: '1px solid var(--sl-border-secondary)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 18, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{s.buyer_name}</span>
              {tierBadge(s.tier)}
            </div>
            <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)', marginTop: 4 }}>
              {s.order_id ? (
                <a href={`/teams/${team.id}/orders/${s.order_id}`} target="_blank" rel="noopener noreferrer"
                  style={{ color: 'var(--sl-fg-brand)', fontWeight: 600, textDecoration: 'none' }}>Order #{s.order_id}</a>
              ) : null}
              {s.order_id && s.purchased_on ? ' · ' : null}
              {s.purchased_on}
            </div>
          </div>
          <button type="button" aria-label="Close" onClick={() => setSelectedId(null)}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--sl-text-tertiary)', fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>Event</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{s.event_name || '—'}</div>
              {s.event_meta ? <div style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>{s.event_meta}</div> : null}
            </div>
            <div>
              <div style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>Order</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-primary)' }}>
                {fmt(s.amount_in_cents)}{s.ticket_count ? ` · ${s.ticket_count} ${s.ticket_count === 1 ? 'ticket' : 'tickets'}` : ''}
              </div>
              {cardLine ? <div style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>{cardLine}</div> : null}
              {s.buyer_postal_code ? <div style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>Billing ZIP {s.buyer_postal_code}</div> : null}
            </div>
          </div>

          {s.refund_window_note ? (
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12, borderRadius: 8, background: inWindow ? 'var(--sl-bg-brand-primary)' : 'var(--sl-bg-warning-primary)', border: `1px solid ${inWindow ? 'var(--sl-border-brand)' : 'var(--sl-border-warning)'}` }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={bannerColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
              <span style={{ fontSize: 14, color: 'var(--sl-text-secondary)' }}>{s.refund_window_note}</span>
            </div>
          ) : null}

          {(hasScore || isEfw) ? (
            <div>
              <div style={SECTION_LABEL}>Opendate Radar (powered by Stripe)</div>
              <div style={{ border: '1px solid var(--sl-border-secondary)', borderRadius: 8, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {hasScore ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 14, color: 'var(--sl-text-secondary)' }}>Risk score</span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: riskColor(s.risk_score) }}>{s.risk_score} · {riskLabel(s.risk_score)}</span>
                    </div>
                    <div style={{ height: 8, background: 'var(--sl-border-secondary)', borderRadius: 9999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 9999, background: riskColor(s.risk_score), width: `${Math.min(s.risk_score, 100)}%` }} />
                    </div>
                  </>
                ) : null}
                {isEfw ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingTop: 2 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--sl-text-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4M12 17h.01" /></svg>
                    <span style={{ fontSize: 14, color: 'var(--sl-text-secondary)' }}>
                      <strong>Early fraud warning received {relativeReceived(s.created_at)}.</strong>{' '}
                      Issuer reported: {(s.fraud_type || '').replace(/_/g, ' ')}.{s.status === 'open' ? ' Refunding now usually prevents the chargeback.' : ''}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {(s.signal_tags || []).length > 0 ? (
            <div>
              <div style={SECTION_LABEL}>Opendate signals</div>
              <div style={{ border: '1px solid var(--sl-border-secondary)', borderRadius: 8, overflow: 'hidden' }}>
                {s.signal_tags.map((rawSig, i) => {
                  const sig = normalizeSignal(rawSig);
                  return (
                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 16px', borderBottom: i < s.signal_tags.length - 1 ? '1px solid var(--sl-border-tertiary)' : 'none' }}>
                    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 9999, background: SEVERITY_DOT[sig.severity] || 'var(--sl-text-quaternary)', flexShrink: 0, marginTop: 6 }} />
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-primary)' }}>
                        {sig.severity ? <span style={SR_ONLY}>{sig.severity} severity: </span> : null}
                        {sig.label}
                      </span>
                      {sig.detail ? <span style={{ fontSize: 13, color: 'var(--sl-text-tertiary)' }}>{sig.detail}</span> : null}
                    </span>
                  </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {s.status === 'open' ? (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={blocklist} onChange={(e) => setBlocklist(e.target.checked)} style={{ marginTop: 2 }} />
              <span style={{ fontSize: 14, color: 'var(--sl-text-secondary)' }}>Add card and email to the Opendate Radar block list so this buyer can&apos;t purchase again</span>
            </label>
          ) : null}
        </div>

        {s.status === 'open' && canAdminister ? (
          <div style={{ padding: '16px 24px 20px', borderTop: '1px solid var(--sl-border-secondary)', display: 'flex', gap: 12 }}>
            <button type="button" disabled={busy} onClick={() => doDismiss(s)}
              style={{ flex: 1, padding: '10px 12px', borderRadius: 8, ...BTN_SECONDARY, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Dismiss flag</button>
            <button type="button" disabled={busy} onClick={() => setConfirmId(s.id)}
              style={{ flex: 2, padding: '10px 12px', borderRadius: 8, ...BTN_DANGER, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Refund {fmt(s.amount_in_cents)} &amp; invalidate</button>
          </div>
        ) : null}
      </div>
    );
  };

  if (view === 'automation') {
    return <AutomationRules team={team} canAdminister={canAdminister} rulesAvailable={rulesAvailable} automationEnabled={automationEnabled} autoRefundAvailable={autoRefundAvailable} signalAction={signalAction} digest={{ membershipId: digest.membershipId, subscribed: digestSubscribed }} onDigestChange={setDigestSubscribed} onBack={() => setView('flagged')} />;
  }

  const chargedBack = metricsData.charged_back_90d || NO_CHARGEBACKS;

  return (
    <div style={{ paddingBottom: 200 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 24, marginBottom: 24 }}>
        <Metric label="Flagged orders" big={metricsData.flagged_count} sub="open" />
        <Metric label="At-risk revenue" big={fmt(metricsData.at_risk_cents)} sub="open orders" />
        <Metric label="Refunded · 30 days" big={metricsData.refunded_30d.count} sub={fmt(metricsData.refunded_30d.cents)} />
        <Metric label="Chargebacks prevented · 90 days" big={metricsData.prevented_90d.count} sub={`${fmt(metricsData.prevented_90d.cents)} saved`} color="var(--sl-text-success)" />
        <Metric label="Charged back after flagging · 90 days" big={chargedBack.count} sub={chargedBackSub(chargedBack, fmt)} color="var(--sl-text-error)" />
      </div>

      <div style={{ ...CARD, overflow: 'hidden' }}>
        {riskRows.rows.length > 0 ? (
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--sl-border-secondary)' }}>
            <RiskDesk {...riskRows} bare />
          </div>
        ) : null}

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '20px 24px', borderBottom: '1px solid var(--sl-border-secondary)', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--sl-text-primary)' }}>Flagged orders</div>
            <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)' }}>Orders likely to end in a fraudulent chargeback. Refund and invalidate tickets before the show to avoid the dispute.</div>
          </div>
          <label style={SR_ONLY} htmlFor="event-scope-filter">Filter by event</label>
          <select id="event-scope-filter" value={eventScope} onChange={selectEventScope}
            style={{ height: 36, padding: '0 12px', fontSize: 14, fontWeight: 600, cursor: 'pointer', borderRadius: 8, background: 'var(--sl-bg-primary)', color: 'var(--sl-text-secondary)', border: '1px solid var(--sl-border-primary)' }}>
            {EVENT_SCOPES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <div style={{ display: 'inline-flex', gap: 4, padding: 4, background: 'var(--sl-bg-secondary)', border: '1px solid var(--sl-border-secondary)', borderRadius: 10 }}>
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => selectFilter(f.key)}
                style={{
                  height: 36, padding: '8px 12px', fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer', borderRadius: 6,
                  background: filter === f.key ? 'var(--sl-bg-primary)' : 'transparent', color: filter === f.key ? 'var(--sl-text-secondary)' : 'var(--sl-text-quaternary)',
                  boxShadow: filter === f.key ? '0 1px 3px rgba(10,13,18,0.1)' : 'none',
                }}>{f.label}</button>
            ))}
          </div>
          {canAdminister ? (
            <button type="button" onClick={() => setView('automation')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 44, padding: '8px 16px', fontSize: 14, fontWeight: 600, borderRadius: 10, cursor: 'pointer', background: 'var(--sl-bg-primary)', color: 'var(--sl-text-secondary)', border: '1px solid var(--sl-border-secondary)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" /></svg>
              Automation rules
            </button>
          ) : null}
        </div>

        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              {['Order', 'Event', 'Flag', 'Signals', 'Amount', ''].map((h, i) => (
                <th key={h || i} style={{ textAlign: i === 4 ? 'right' : 'left', borderBottom: '1px solid var(--sl-border-secondary)', background: 'var(--sl-bg-secondary)', padding: '12px 16px', fontSize: 12, color: 'var(--sl-text-tertiary)', fontWeight: 400 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const resolved = row.status !== 'open';
              return (
                <tr key={row.id} data-testid={`flag-row-${row.id}`}
                  role="button" tabIndex={0}
                  onClick={() => setSelectedId(row.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(row.id); } }}
                  style={{ cursor: 'pointer', background: resolved ? 'var(--sl-bg-secondary)' : 'var(--sl-bg-primary)' }}>
                  <td style={{ padding: '16px', borderBottom: '1px solid var(--sl-border-secondary)' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{row.buyer_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>{row.customer_email}</div>
                  </td>
                  <td style={{ padding: '16px', borderBottom: '1px solid var(--sl-border-secondary)' }}>
                    <div style={{ fontSize: 14, color: 'var(--sl-text-primary)' }}>{row.event_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>{row.event_meta}</div>
                  </td>
                  <td style={{ padding: '16px', borderBottom: '1px solid var(--sl-border-secondary)' }}>{tierBadge(row.tier)}</td>
                  <td style={{ padding: '16px', borderBottom: '1px solid var(--sl-border-secondary)', maxWidth: 280 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(row.signal_tags || []).map((rawSig, i) => {
                        const sig = normalizeSignal(rawSig);
                        return (
                        <span key={i} title={sig.detail} aria-label={sig.detail ? `${sig.label}: ${sig.detail}` : sig.label} style={{ padding: '3px 8px', border: '1px solid var(--sl-border-primary)', borderRadius: 6, fontSize: 12, color: 'var(--sl-text-secondary)' }}>{sig.label}</span>
                        );
                      })}
                    </div>
                  </td>
                  <td style={{ padding: '16px', borderBottom: '1px solid var(--sl-border-secondary)', textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{fmt(row.amount_in_cents)}</div>
                  </td>
                  <td style={{ padding: '16px', borderBottom: '1px solid var(--sl-border-secondary)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {row.status === 'open' && canAdminister ? (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedId(row.id); setConfirmId(row.id); }}
                        style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--sl-bg-primary)', color: 'var(--sl-text-error)', border: '1px solid var(--sl-border-error)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Refund &amp; invalidate</button>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>{resolvedLabel(row)}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && rows.length === 0 ? (
          <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--sl-text-tertiary)' }}>
            {loadError ? 'Could not load flagged orders. Please refresh.' : 'No flagged orders match this filter.'}
          </div>
        ) : null}
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--sl-border-secondary)' }}>
          <TeamPagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total_entries}
            pageSize={pagination.per_page}
            itemLabel="flagged orders"
            onPageChange={(p) => setPage(p)}
          />
        </div>
      </div>

      <SlidingPane
        isOpen={!!selected}
        width="420px"
        hideHeader
        className="sliding-pane-container-full-height"
        onRequestClose={() => setSelectedId(null)}
      >
        {renderDrawer()}
      </SlidingPane>

      <Popup modal arrow={false} open={!!confirmRow} closeOnDocumentClick onClose={() => setConfirmId(null)}
        contentStyle={{ width: 'auto', maxWidth: 420, padding: 0, border: 'none', background: 'transparent', boxShadow: 'none' }}>
        {confirmRow ? (
          <div style={{ background: 'var(--sl-bg-primary)', borderRadius: 12, padding: 24, boxShadow: '0 20px 24px -4px rgba(10,13,18,0.1)' }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--sl-text-primary)' }}>Refund order and invalidate tickets?</div>
            <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)', margin: '8px 0 24px' }}>
              {confirmRow.buyer_name} will be refunded <strong>{fmt(confirmRow.amount_in_cents)}</strong> and their tickets will stop scanning at the door immediately. The refund is reported to Opendate as fraudulent.
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" disabled={busy} onClick={() => setConfirmId(null)}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, ...BTN_SECONDARY, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button type="button" disabled={busy} onClick={doRefund}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, ...BTN_DANGER, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Refund {fmt(confirmRow.amount_in_cents)}</button>
            </div>
          </div>
        ) : null}
      </Popup>
    </div>
  );
};

RiskChargebacks.propTypes = {
  team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }).isRequired,
  canAdminister: PropTypes.bool,
  currencyCode: PropTypes.string,
  metrics: PropTypes.shape({
    flagged_count: PropTypes.number,
    at_risk_cents: PropTypes.number,
    refunded_30d: PropTypes.shape({ count: PropTypes.number, cents: PropTypes.number }),
    prevented_90d: PropTypes.shape({ count: PropTypes.number, cents: PropTypes.number }),
    charged_back_90d: PropTypes.shape({ count: PropTypes.number, cents: PropTypes.number, median_days: PropTypes.number }),
  }).isRequired,
  blockListDefault: PropTypes.bool,
  digest: PropTypes.shape({
    membershipId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    subscribed: PropTypes.bool,
  }),
  rulesAvailable: PropTypes.bool,
  automationEnabled: PropTypes.bool,
  autoRefundAvailable: PropTypes.bool,
  signalAction: PropTypes.string,
  riskRows: PropTypes.shape({
    rows: PropTypes.array,
    endpoints: PropTypes.object,
    currencyCode: PropTypes.string,
    teamScoped: PropTypes.bool,
    canAct: PropTypes.bool,
  }),
};

RiskChargebacks.defaultProps = { canAdminister: false, currencyCode: 'USD', blockListDefault: true, digest: { membershipId: null, subscribed: false }, rulesAvailable: false, automationEnabled: false, autoRefundAvailable: false, signalAction: 'flag', riskRows: { rows: [] } };

export default RiskChargebacks;
